import type {
  Lobby,
  LobbyAPI,
  LobbyCategory,
  LobbyGame,
} from '../../../../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { getClickCount } from '../../../../../pages/lobby-sh/rank-helper';
import { mapLobby } from './content-mapper';

export const FRONTEND_ONLY_CATEGORY_PREFIX = 'frontend-only--';

export const FRONTEND_ONLY_CATEGORY = {
  ACTIVITY_LANE: `${FRONTEND_ONLY_CATEGORY_PREFIX}activity-lane`,
  USER_AREA: `${FRONTEND_ONLY_CATEGORY_PREFIX}user-area`,
  // One banner slot per audience; the view renders whichever matches login.
  BEST_MATCH_BANNER_LOGGED_IN: `${FRONTEND_ONLY_CATEGORY_PREFIX}best-match-banner-logged-in`,
  BEST_MATCH_BANNER_LOGGED_OUT: `${FRONTEND_ONLY_CATEGORY_PREFIX}best-match-banner-logged-out`,
} as const;

/**
 * The order of the top items in the lobby.
 * This is used to ensure that these items are always displayed at the top of the list.
 * Each entry matches a category by slug or filterId.
 */
const topItems_order: string[] = [
  FRONTEND_ONLY_CATEGORY.ACTIVITY_LANE,
  'premium-slots',
  'fur-dich',
  FRONTEND_ONLY_CATEGORY.USER_AREA,
  FRONTEND_ONLY_CATEGORY.BEST_MATCH_BANNER_LOGGED_IN,
  // Popular-slots lane: the slug differs per lobby (staging `beliebte-spiele`, prod `beliebte-online-slots`), the filterId does not.
  'COMBO-BeliebteOnlineSlots',
  FRONTEND_ONLY_CATEGORY.BEST_MATCH_BANNER_LOGGED_OUT,
];

function matchesTopItem(category: LobbyCategory, key: string): boolean {
  return category.slug === key || category.filterId === key;
}

/**
 * Sorts an array of items with a specific order for top items.
 */
export function sortWithTopItems(categories: LobbyCategory[]): LobbyCategory[] {
  const topItems: LobbyCategory[] = [];
  const otherItems: LobbyCategory[] = [];

  for (const category of categories) {
    if (topItems_order.some((key) => matchesTopItem(category, key))) {
      topItems.push(category);
    } else {
      otherItems.push(category);
    }
  }

  // Ensure the top items are in the desired order
  const sortedTopItems = topItems_order
    .map((key) => topItems.find((category) => matchesTopItem(category, key)))
    .filter((category) => category !== undefined) as LobbyCategory[];

  const sortedCategories = [...sortedTopItems, ...otherItems];

  return sortedCategories;
}

/**
 * Add placeholder categories to enable top items sorting
 */
export function addPlaceholderCategories(categories: LobbyCategory[]): LobbyCategory[] {
  categories.push(
    // @ts-expect-error
    {
      filterId: FRONTEND_ONLY_CATEGORY.ACTIVITY_LANE,
      slug: FRONTEND_ONLY_CATEGORY.ACTIVITY_LANE,
    },
    {
      filterId: FRONTEND_ONLY_CATEGORY.USER_AREA,
      slug: FRONTEND_ONLY_CATEGORY.USER_AREA,
    },
    {
      filterId: FRONTEND_ONLY_CATEGORY.BEST_MATCH_BANNER_LOGGED_IN,
      slug: FRONTEND_ONLY_CATEGORY.BEST_MATCH_BANNER_LOGGED_IN,
    },
    {
      filterId: FRONTEND_ONLY_CATEGORY.BEST_MATCH_BANNER_LOGGED_OUT,
      slug: FRONTEND_ONLY_CATEGORY.BEST_MATCH_BANNER_LOGGED_OUT,
    },
  );
  return categories;
}

/**
 * Adapt lobby response to the frontend needs.
 */
export function prepareLobby(lobby: LobbyAPI): Lobby {
  const adaptedLobby: Lobby = mapLobby(lobby);
  // Add placeholder categories for top items if the state is 'de-games' to ensure they are always present and can be sorted to the top.
  if (window.$app.appConfig.state === 'de-games') {
    adaptedLobby.categories = addPlaceholderCategories(adaptedLobby.categories);
    adaptedLobby.categories = sortWithTopItems(adaptedLobby.categories);
  }
  if (window.$app.appConfig.state === 'de-sh') {
    // Use only the first category for SH.
    adaptedLobby.categories = adaptedLobby.categories.slice(0, 1);
    if (adaptedLobby.categories[0]?.games?.length) {
      adaptedLobby.categories[0].games = sortGamesByUserActivity(adaptedLobby.categories[0].games);
    }
  }
  return adaptedLobby;
}

/**
 * Sort games based on user activity (local storage).
 */
function sortGamesByUserActivity(games: LobbyGame[]): LobbyGame[] {
  const clickCount = getClickCount();
  games.sort((a, b) => {
    const aClicks = clickCount.find((activity) => activity.gameId === a.id)?.clickCount ?? 0;
    const bClicks = clickCount.find((activity) => activity.gameId === b.id)?.clickCount ?? 0;
    return bClicks - aClicks; // Sort in descending order of clicks
  });
  return games;
}
